/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import {
    NSDropdown, NSInput, NSLabel, NSSelect,
} from 'bricks';
import NSInputNumberStepper from 'bricks/NSInputNumberStepper/NSInputNumberStepper';
import {
    parkingOptions, industrialParkingOptions, STRUCTURED_PARKING, SURFACE_PARKING, UNDERGROUND_PARKING, TRAILER_PARKING,
} from 'views/ProFormaTable/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import CalculateSizeCard from 'views/ProFormaTable/components/ProForma/ProFormaGeneralInformation/CalculateSizeCard/CalculateSizeCard';
import { numeralFormatter } from 'ns_libs/formatter';
import { ProFormaModelIds } from 'constants/proForma';
import ProFormaSizeOptionalFields from 'views/ProFormaTable/components/ProForma/ProFormaGeneralInformation/ProFormaSizeDetails/ProFormaSizeOptionalFields/ProFormaSizeOptionalFields';
import './BasicInformation.scss';
import { useGrossBuildableArea } from 'views/ProFormaWizard/BasicInformation/hooks/useGrossBuildableArea';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import { initialBasicInformationValues, QUICK_START_LABEL_CLASS_NAME } from '../constants';
import { BasicInformationValues } from '../types';
import { updateParkingAndSpaces } from '../helpers';

const LAND_AREA_UNITS = [
    { value: 'acres', label: 'acres' },
    { value: 'sqft', label: 'ft²' },
];

interface BasicInformationProps {
    values: BasicInformationValues;
    proFormaModelId: number;
    onUpdate: (data: any) => void;
}

const BasicInformation: React.FC<BasicInformationProps> = ({ values, onUpdate, proFormaModelId }) => {
    const [formData, setFormData] = useState(() => values || initialBasicInformationValues);

    useEffect(() => {
        if (onUpdate) {
            onUpdate(formData);
        }
    }, [formData, onUpdate]);

    const {
        lots, createLot, updateLot, deleteLot, totalGrossBuildableArea,
    } = useGrossBuildableArea([]);

    const handleInputChange = (field: string, value: any) => {
        setFormData((prev: BasicInformationValues) => {
            if (field === 'parking') {
                const updatedFormData = updateParkingAndSpaces(prev, value);
                return updatedFormData;
            }
            return { ...prev, [field]: value };
        });
    };

    const toggleIsFAR = (value: boolean) => {
        setFormData((prev: any) => ({ ...prev, isFAR: value }));
    };

    const toggleCalculateArea = () => {
        setFormData((prev: BasicInformationValues) => ({ ...prev, calculateArea: !prev.calculateArea }));
        if (!formData.calculateArea && lots.length === 0) {
            createLot();
        }
    };

    return (
        <div className="BasicInformation p-3">
            <div className="BasicInformation__form">

                {/* START DATE SECTION */}
                <div>
                    <div className="BasicInformation__title">Start Date</div>
                    <div className="BasicInformation__subtitle">
                        Determine start date for this analysis. Typically this is your acquisition closing. This date will be used as
                        period 1 for cash flows.
                    </div>
                    <NSInput
                        type="month"
                        name="startDate"
                        value={formData.startDate ? new Date(formData.startDate).toISOString()?.slice(0, 7) : ''}
                        onChange={(e: { target: { value: any; }; }) => handleInputChange('startDate', e.target.value ? new Date(`${e.target.value}-01T00:00:00`) : null)}
                        inputClassName="BasicInformation__input-width mt-2"
                    />
                </div>

                <div className="BasicInformation__separator" />

                {/* SIZE SECTION */}
                <div>
                    <div className="BasicInformation__title">Size</div>
                    <div className="BasicInformation__subtitle">Details about this investment's property size and building</div>

                    {/* Land Area */}
                    <div className="d-flex mt-2 w-75">
                        <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Land area</NSLabel>
                        <div className="d-flex flex-column w-50">
                            <NSInput
                                id="landArea"
                                type="number"
                                name="landArea"
                                placeholder="Enter land area"
                                value={formData.landArea || ''}
                                inputGroupClassName="p-0"
                                onChange={(e: { target: { value: string; }; }) => handleInputChange('landArea', e.target.value === '' ? 0 : Number(e.target.value))}
                                appendInputAddon={(
                                    <NSDropdown
                                        options={LAND_AREA_UNITS}
                                        selectedOption={LAND_AREA_UNITS.find(option => option.value === formData.landAreaUnit)}
                                        customSelectedOptionText={LAND_AREA_UNITS.find(option => option.value === formData.landAreaUnit)?.label || ''}
                                        handleSelectOption={option => {
                                            const selectedUnit = option.value;
                                            let convertedArea = formData.landArea;
                                            if (selectedUnit === 'sqft' && formData.landAreaUnit === 'acres') {
                                                convertedArea *= 43560;
                                            } else if (selectedUnit === 'acres' && formData.landAreaUnit === 'sqft') {
                                                convertedArea /= 43560;
                                            }
                                            setFormData((prev: any) => ({ ...prev, landArea: Number(convertedArea.toFixed(2)), landAreaUnit: selectedUnit }));
                                        }}
                                        badgeColor=""
                                        isBadgeToggle
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* FAR / GBA Toggle */}
                    {/* TODO: replace this section with ProFormaSizeAreaFields component */}
                    <div className="d-flex my-2 w-75">
                        <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>
                            <div className="d-flex">
                                <NSButtonBase
                                    color="secondary"
                                    outline={false}
                                    callback={() => toggleIsFAR(true)}
                                    text="FAR"
                                    className={formData.isFAR ? 'border border-primary bg-primary-lighten' : ''}
                                />
                                <NSButtonBase
                                    color="secondary"
                                    outline={false}
                                    callback={() => toggleIsFAR(false)}
                                    text="Gross build. area"
                                    className={!formData.isFAR ? 'border border-primary bg-primary-lighten' : ''}
                                />
                            </div>
                        </NSLabel>
                        {/* FAR Input */}
                        <RenderIf isTrue={formData.isFAR}>
                            <div className="d-flex flex-column w-50">
                                <NSInputNumberStepper
                                    id="floorAreaRatio"
                                    name="floorAreaRatio"
                                    value={Number(numeralFormatter(formData.floorAreaRatio, '0.00'))}
                                    onChange={(count: number) => handleInputChange('floorAreaRatio', count)}
                                    min={0.1}
                                    step={0.1}
                                    inputGroupClassName="BasicInformation__input-width"
                                />
                            </div>
                        </RenderIf>
                    </div>

                    {/* Gross Buildable Area Input */}
                    <RenderIf isTrue={!formData.isFAR}>
                        <div className="d-flex flex-column w-50">
                            <NSInput
                                id="grossBuildableArea"
                                type="number"
                                name="grossBuildableArea"
                                value={formData.grossBuildableArea === 0 ? '' : formData.grossBuildableArea}
                                onChange={(e: { target: { value: string; }; }) => handleInputChange('grossBuildableArea', e.target.value === '' ? 0 : Number(e.target.value))}
                                placeholder="Enter gross buildable area"
                                inputGroupClassName="p-0"
                                appendInputAddon={(
                                    <NSDropdown
                                        options={LAND_AREA_UNITS}
                                        selectedOption={LAND_AREA_UNITS.find(option => option.value === formData.grossBuildableUnit)}
                                        customSelectedOptionText={LAND_AREA_UNITS.find(option => option.value === formData.grossBuildableUnit)?.label || ''}
                                        handleSelectOption={option => {
                                            const selectedUnit = option.value;
                                            let convertedArea = formData.grossBuildableArea;
                                            if (selectedUnit === 'sqft' && formData.grossBuildableUnit === 'acres') {
                                                convertedArea *= 43560;
                                            } else if (selectedUnit === 'acres' && formData.grossBuildableUnit === 'sqft') {
                                                convertedArea /= 43560;
                                            }
                                            setFormData((prev: any) => ({ ...prev, grossBuildableArea: Number(convertedArea.toFixed(2)), grossBuildableUnit: selectedUnit }));
                                        }}
                                        badgeColor=""
                                        isBadgeToggle
                                    />
                                )}
                            />
                        </div>
                    </RenderIf>

                    {/* CalculateSizeCard Toggle */}
                    <RenderIf isTrue={!formData.isFAR}>
                        <div className="w-75">
                            <CalculateSizeCard
                                lots={lots}
                                onCreateLot={createLot}
                                onDeleteLot={(id: number) => {
                                    deleteLot(id);
                                    if (lots.length === 1) {
                                        toggleCalculateArea();
                                    }
                                }}
                                onUpdateLot={updateLot}
                                totalGrossBuildableArea={totalGrossBuildableArea}
                                toggleShowLots={toggleCalculateArea}
                                showLots={formData.calculateArea}
                            />
                        </div>
                    </RenderIf>

                    <RenderIf isTrue={proFormaModelId !== ProFormaModelIds.APARTMENT}>
                        {/* Buildings - only show for non-apartment models */}
                        <div className="d-flex align-items-center mt-2 w-75">
                            <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Buildings</NSLabel>
                            <div className="d-flex flex-column">
                                <NSInputNumberStepper
                                    id="buildings"
                                    name="buildings"
                                    value={formData.buildings}
                                    onChange={(count: number) => handleInputChange('buildings', count)}
                                    min={1}
                                    step={1}
                                    inputGroupClassName="BasicInformation__input-width"
                                />
                            </div>
                        </div>

                        {/* Built Gross SF - only show for non-apartment models */}
                        <div className="d-flex align-items-center mt-2 w-75">
                            <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Built gross SF</NSLabel>
                            <div className="d-flex flex-column w-50">
                                <NSInput
                                    id="builtGrossSquareFootage"
                                    name="builtGrossSquareFootage"
                                    placeholder="Enter built gross SF"
                                    value={formData.builtGrossSquareFootage || ''}
                                    appendInputAddon={<div className="px-2">ft²</div>}
                                    type="number"
                                    onChange={(e: { target: { value: string; }; }) => handleInputChange('builtGrossSquareFootage', e.target.value === '' ? 0 : Number(e.target.value))}
                                    inputGroupClassName="p-0"
                                />
                            </div>
                        </div>
                    </RenderIf>

                </div>
                {/* STORIES SECTION - only show for non-apartment models */}
                <RenderIf isTrue={proFormaModelId !== ProFormaModelIds.APARTMENT}>
                    <div className="d-flex align-items-center mt-2 w-75">
                        <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Stories</NSLabel>
                        <div className="d-flex flex-column">
                            <NSInputNumberStepper
                                id="stories"
                                name="stories"
                                value={formData.stories}
                                onChange={(count: number) => handleInputChange('stories', count)}
                                min={1}
                                step={1}
                                inputGroupClassName="BasicInformation__input-width"
                            />
                        </div>
                    </div>
                </RenderIf>

                {/* PARKING SECTION */}
                <div className="d-flex align-items-center mt-2 w-75">
                    <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Parking</NSLabel>
                    <div className="w-100">
                        <NSSelect
                            isMulti
                            name="parking"
                            options={proFormaModelId === ProFormaModelIds.INDUSTRIAL ? industrialParkingOptions : parkingOptions}
                            value={formData.parking}
                            onChange={options => handleInputChange('parking', options || [])}
                            className="w-75"
                        />
                    </div>
                </div>

                {/* PARKING SPACES based on parking selection */}
                {formData.parking?.some((p: any) => p.value === STRUCTURED_PARKING) && (
                    <div className="d-flex my-2 w-75">
                        <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Structured Parking Spaces</NSLabel>
                        <NSInputNumberStepper
                            id="structuredParkingSpaces"
                            name="structuredParkingSpaces"
                            value={formData.structuredParkingSpaces}
                            onChange={(count: number) => handleInputChange('structuredParkingSpaces', count)}
                            min={0}
                            step={1}
                            inputGroupClassName="BasicInformation__input-width"
                        />
                    </div>
                )}

                {formData.parking?.some((p: any) => p.value === SURFACE_PARKING) && (
                    <div className="d-flex my-2 w-75">
                        <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Surface Parking Spaces</NSLabel>
                        <NSInputNumberStepper
                            id="surfaceParkingSpaces"
                            name="surfaceParkingSpaces"
                            value={formData.surfaceParkingSpaces}
                            onChange={(count: number) => handleInputChange('surfaceParkingSpaces', count)}
                            min={0}
                            step={1}
                            inputGroupClassName="BasicInformation__input-width"
                        />
                    </div>
                )}

                {formData.parking?.some((p: any) => p.value === UNDERGROUND_PARKING) && (
                    <div className="d-flex my-2 w-75">
                        <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Underground Parking Spaces</NSLabel>
                        <NSInputNumberStepper
                            id="undergroundParkingSpaces"
                            name="undergroundParkingSpaces"
                            value={formData.undergroundParkingSpaces}
                            onChange={(count: number) => handleInputChange('undergroundParkingSpaces', count)}
                            min={0}
                            step={1}
                            inputGroupClassName="BasicInformation__input-width"
                        />
                    </div>
                )}

                {formData.parking?.some((p: any) => p.value === TRAILER_PARKING) && (
                    <div className="d-flex my-2 w-75">
                        <NSLabel className={QUICK_START_LABEL_CLASS_NAME}>Trailer Parking Spaces</NSLabel>
                        <NSInputNumberStepper
                            id="trailerParkingSpaces"
                            name="trailerParkingSpaces"
                            value={formData.trailerParkingSpaces}
                            onChange={(count: number) => handleInputChange('trailerParkingSpaces', count)}
                            min={0}
                            step={1}
                            inputGroupClassName="BasicInformation__input-width"
                        />
                    </div>
                )}

                {/* Optional fields section for apartment models */}
                <RenderIf isTrue={proFormaModelId === ProFormaModelIds.APARTMENT}>
                    <ProFormaSizeOptionalFields
                        values={formData}
                        handleInputChange={handleInputChange}
                        rowClassName="d-flex align-items-center mt-2 w-75"
                        labelClassName={QUICK_START_LABEL_CLASS_NAME}
                        inputClassName="BasicInformation__input-width"
                        useSocketContext={false}
                    />
                </RenderIf>

                {/* ASSUMPTIONS SECTION */}
                <div className="BasicInformation__separator" />
                <div className="BasicInformation__title">Assumptions</div>
                <div className="BasicInformation__subtitle">Assumptions about the sale of this investment</div>
                <div className="w-50">
                    {/* Sale Month */}
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="BasicInformation__label my-auto text-white font-weight-normal mr-2">Sale month</NSLabel>
                        <div className="d-flex flex-column w-50">
                            <NSInput
                                type="month"
                                name="saleDate"
                                value={formData.saleDate ? new Date(formData.saleDate).toISOString().slice(0, 7) : ''}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const { value } = event.target;
                                    const date = value ? new Date(`${value}-01T00:00:00`) : null;
                                    handleInputChange('saleDate', date);
                                }}
                                inputClassName="BasicInformation__input-width"
                            />
                        </div>
                    </div>

                    {/* Current Market Cap Rate */}
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="BasicInformation__label my-auto text-white font-weight-normal mr-2">
                            Current market cap rate
                        </NSLabel>
                        <div className="d-flex flex-column">
                            <NSInputNumberStepper
                                id="currentMarketCapRate"
                                name="currentMarketCapRate"
                                value={Number(numeralFormatter(formData.currentMarketCapRate, '0.00'))}
                                onChange={(count: number) => handleInputChange('currentMarketCapRate', count)}
                                min={0.1}
                                step={0.1}
                                percentage
                                inputGroupClassName="BasicInformation__input-width"
                            />
                        </div>
                    </div>

                    {/* Projected Cap Rate at Sale */}
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="BasicInformation__label my-auto text-white font-weight-normal mr-2">
                            Projected cap rate at sale
                        </NSLabel>
                        <div className="d-flex flex-column">
                            <NSInputNumberStepper
                                id="projectedCapRateAtSale"
                                name="projectedCapRateAtSale"
                                value={Number(numeralFormatter(formData.projectedCapRateAtSale, '0.00'))}
                                onChange={(count: number) => handleInputChange('projectedCapRateAtSale', count)}
                                min={0.1}
                                step={0.1}
                                percentage
                                inputGroupClassName="BasicInformation__input-width"
                            />
                        </div>
                    </div>

                    {/* Selling Costs */}
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="BasicInformation__label my-auto text-white font-weight-normal mr-2">
                            Selling costs
                        </NSLabel>
                        <div className="d-flex flex-column">
                            <NSInputNumberStepper
                                id="sellingCosts"
                                name="sellingCosts"
                                value={Number(numeralFormatter(formData.sellingCosts, '0.00'))}
                                onChange={(count: number) => handleInputChange('sellingCosts', count)}
                                min={1}
                                step={1}
                                percentage
                                inputGroupClassName="BasicInformation__input-width"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BasicInformation;
